import { Component } from "react";
import { clearSession } from "@/lib/session-store";

/**
 * Last line of defence against a blank window.
 *
 * Without a boundary, one uncaught render error unmounts the whole tree and a
 * pilot is left looking at the bare background (black in the desktop app)
 * with no message and no way out except quitting. This shows what happened
 * and two ways forward. "Sign out" clears the stored session as well, which
 * gets a pilot out of a crash caused by stale data in it.
 */
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  signOut = () => {
    try {
      clearSession();
      window.electronAPI?.session?.clear?.();
    } catch (_) { /* nothing more to clear */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8" data-testid="error-boundary">
        <div className="card-flat max-w-lg w-full p-6">
          <div className="overline mb-1">VACARS5</div>
          <h1 className="text-lg font-bold mb-2">Something went wrong on this screen</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Your flight data is not affected. Reloading usually fixes it; if it
            keeps happening, sign out and back in.
          </p>
          <pre className="text-xs font-mono bg-secondary text-muted-foreground rounded-md p-3 mb-5 whitespace-pre-wrap break-words">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md hover:brightness-110"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.signOut}
              className="text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-md border border-border hover:bg-secondary"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
}
